import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { RequireEvents } from '../components/DependencyGate';
import {
  Datagrid,
  List,
  Create,
  Edit,
  Tab,
  TextField,
  NumberField,
  ArrayField,
  FunctionField,
  ReferenceField,
  Show,
  TabbedShowLayout,
  TabbedForm,
  FormTab,
  TextInput,
  DateInput,
  ArrayInput,
  SimpleFormIterator,
  NumberInput,
  SelectInput,
  ReferenceInput,
  AutocompleteInput,
  usePermissions,
  useNotify,
  useDataProvider,
  useRecordContext,
  useGetOne,
  useGetList,
  useRedirect,
  Button,
  SaveButton,
  Toolbar,
  Link,
} from 'react-admin';
import { useWatch } from 'react-hook-form';
import {
  Box,
  Typography,
  TextField as MuiTextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Alert,
  Chip,
  Stack,
  Divider,
  Card,
  CardActionArea,
  CardContent,
  MenuItem,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ReferenceLine, ResponsiveContainer } from 'recharts';
import EditIcon from '@mui/icons-material/Edit';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { ArchivedSelect } from '../components/selects';
import { can, type Permissions, type ScoreTable, type MeasurementDef } from '../types';
import { generateScoreTableRows } from '../utils/generateScoreTableRows';
import {
  computeCoverageWarnings,
  hasDemographicScoreFields,
  parseScoreTableCsv,
  normalizeRow,
  validateBandRows,
  sortVersionHistory,
  submitCsvImport,
  submitGenerate,
  submitManual,
  d10CurveData,
  statusChipProps,
  type RawBandRow,
  type CoverageRow,
} from '../utils/scoreTableHelpers';

// Re-export types for backwards compat / test convenience
export type { CoverageRow, RawBandRow };
export { computeCoverageWarnings, normalizeRow, validateBandRows, sortVersionHistory, submitCsvImport, submitGenerate, submitManual, d10CurveData, statusChipProps };
export type { CsvSubmitPayload, GenerateSubmitPayload, ManualSubmitPayload, NormalizedBandRow, VersionHistoryRecord, D10CurvePoint, StatusChipProps } from '../utils/scoreTableHelpers';

// ---------------------------------------------------------------------------
// StatusChip
// ---------------------------------------------------------------------------

export const StatusChip = ({ status }: { status?: string }) => {
  return <Chip size="small" {...statusChipProps(status)} />;
};

const ScoreTableStatus = ({ record }: { record?: ScoreTable }) => hasDemographicScoreFields(record?.rows)
  ? <Chip size="small" color="warning" label="Historical — ineligible" />
  : <StatusChip status={record?.status} />;

const HistoricalDemographicNotice = () => {
  const record = useRecordContext<ScoreTable>();
  if (!hasDemographicScoreFields(record?.rows)) return null;
  return <Alert severity="warning" role="note">This immutable historical table contains demographic score bands. It is ineligible for active scoring and is shown only as reconstruction evidence.</Alert>;
};

const ScoreTableRowsField = () => {
  const record = useRecordContext<ScoreTable>();
  const historical = hasDemographicScoreFields(record?.rows);
  return <>
    {historical && <HistoricalDemographicNotice />}
    <ArrayField label="Scoring Rows" source="rows">
      <Datagrid bulkActionButtons={false}>
        {historical && <FunctionField label="Historical demographic evidence" render={(row: CoverageRow) => `${row.sex ?? 'unspecified sex'}; ages ${row.age_min ?? 'unspecified'}–${row.age_max ?? 'unspecified'}`} />}
        <NumberField source="result_min" label="Result Min" />
        <NumberField source="result_max" label="Result Max" />
        <NumberField source="points" label="Points" />
      </Datagrid>
    </ArrayField>
  </>;
};

const D10CurveChart = ({
  topResult,
  bottomResult,
  minPts,
  maxPts,
}: {
  topResult: number;
  bottomResult: number;
  minPts: number;
  maxPts: number;
}) => {
  const curveData = d10CurveData(topResult, bottomResult, minPts, maxPts);
  return (
    <Box mt={2} sx={{ height: 200 }}>
      <Typography variant="caption" color="text.secondary">
        D10 piecewise-linear curve (3 segments)
      </Typography>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={curveData} margin={{ top: 10, right: 20, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="result" type="number" domain={['auto', 'auto']} label={{ value: 'Result', position: 'insideBottom', offset: -5 }} />
          <YAxis domain={[minPts, maxPts]} label={{ value: 'Points', angle: -90, position: 'insideLeft' }} />
          <RechartsTooltip />
          <ReferenceLine y={Math.round(minPts + 0.375 * (maxPts - minPts))} stroke="#666" strokeDasharray="4 4" label="375" />
          <ReferenceLine y={Math.round(minPts + 0.625 * (maxPts - minPts))} stroke="#666" strokeDasharray="4 4" label="625" />
          <Line type="linear" dataKey="points" stroke="#1976d2" dot strokeWidth={2} />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// MeasurementNameField — react-admin field component
// ---------------------------------------------------------------------------

export const MeasurementNameField = () => {
  const record = useRecordContext<ScoreTable>();
  const { data: event } = useGetOne(
    'events',
    { id: record?.event_id ?? '' },
    { enabled: !!record?.event_id },
  );
  const measurement: MeasurementDef | undefined = (event?.measurements ?? []).find(
    (m: MeasurementDef) => m.measurement_id === record?.measurement_id,
  );
  return (
    <Typography component="span" variant="body2">
      {measurement?.name ?? record?.measurement_id ?? '—'}
    </Typography>
  );
};

// ---------------------------------------------------------------------------
// DependentMeasurementSelect — reads event_id from form via useWatch
// ---------------------------------------------------------------------------

const DependentMeasurementSelect = ({ disabled }: { disabled?: boolean }) => {
  const eventId = useWatch({ name: 'event_id' });
  const { data: event } = useGetOne(
    'events',
    { id: eventId ?? '' },
    { enabled: !!eventId },
  );
  const measurements: MeasurementDef[] = event?.measurements ?? [];
  const isDisabled = disabled || !eventId || measurements.length === 0;

  if (!eventId || measurements.length === 0) {
    return (
      <TextInput
        fullWidth
        label={isDisabled ? 'Select an event first' : 'Measurement ID'}
        source="measurement_id"
        disabled={isDisabled}
      />
    );
  }

  return (
    <SelectInput
      fullWidth
      label="Measurement"
      source="measurement_id"
      choices={measurements.map((m) => ({ id: m.measurement_id, name: m.name }))}
      disabled={isDisabled}
    />
  );
};

// ---------------------------------------------------------------------------
// ManualBandsPanel
// ---------------------------------------------------------------------------

function newEmptyRow(): RawBandRow {
  return {
    id: crypto.randomUUID(),
    result_min: '',
    result_max: '',
    points: '',
  };
}

const ManualBandsPanel = ({
  eventId,
  measurementId,
  label,
  effectiveDate,
  onBack,
  onSaved,
}: {
  eventId: string;
  measurementId: string;
  label: string;
  effectiveDate: string;
  onBack: () => void;
  onSaved: (newId: string) => void;
}) => {
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [rows, setRows] = useState<RawBandRow[]>([newEmptyRow()]);
  const [errors, setErrors] = useState<string[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const updateWarnings = useCallback(() => {
    const normalized = rows
      .filter((r) => r.result_min !== '' && r.result_max !== '' && r.points !== '')
      .map(normalizeRow);
    setWarnings(computeCoverageWarnings(normalized));
  }, [rows]);

  useEffect(() => {
    updateWarnings();
  }, [updateWarnings]);

  const updateRow = (id: string, field: keyof RawBandRow, value: string) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const addRow = () => setRows((prev) => [...prev, newEmptyRow()]);

  const removeRow = (id: string) => setRows((prev) => prev.filter((r) => r.id !== id));

  const handleSave = async () => {
    const validationErrors = validateBandRows(rows);
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors([]);
    const normalizedRows = rows.map(normalizeRow);
    setWarnings(computeCoverageWarnings(normalizedRows));

    setSaving(true);
    try {
      const result = await submitManual(dataProvider, {
        event_id: eventId,
        measurement_id: measurementId,
        label,
        effective_date: effectiveDate,
        rows: normalizedRows,
        source: 'manual',
      });
      onSaved(result.data.id);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      notify(`Save failed: ${msg}`, { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>Enter scoring bands manually</Typography>

      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.map((e, i) => <div key={i}>{e}</div>)}
        </Alert>
      )}

      {warnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {warnings.map((w, i) => <div key={i}>{w}</div>)}
        </Alert>
      )}

      <Table size="small" sx={{ mb: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell>Result Min *</TableCell>
            <TableCell>Result Max *</TableCell>
            <TableCell>Points *</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell>
                <MuiTextField
                  type="number"
                  size="small"
                  value={row.result_min}
                  onChange={(e) => updateRow(row.id, 'result_min', e.target.value)}
                  sx={{ width: 100 }}
                />
              </TableCell>
              <TableCell>
                <MuiTextField
                  type="number"
                  size="small"
                  value={row.result_max}
                  onChange={(e) => updateRow(row.id, 'result_max', e.target.value)}
                  sx={{ width: 100 }}
                />
              </TableCell>
              <TableCell>
                <MuiTextField
                  type="number"
                  size="small"
                  value={row.points}
                  onChange={(e) => updateRow(row.id, 'points', e.target.value)}
                  sx={{ width: 90 }}
                />
              </TableCell>
              <TableCell>
                <IconButton
                  size="small"
                  onClick={() => removeRow(row.id)}
                  disabled={rows.length === 1}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Stack direction="row" spacing={1} mb={3}>
        <Button
          label="Add Row"
          startIcon={<AddIcon />}
          onClick={addRow}
          variant="outlined"
          size="small"
        />
      </Stack>

      <Stack direction="row" spacing={1}>
        <Button label="Back" onClick={onBack} variant="outlined" size="small" />
        <Button
          label={saving ? 'Saving…' : 'Save'}
          startIcon={<CheckCircleIcon />}
          onClick={handleSave}
          disabled={saving}
          variant="contained"
          size="small"
        />
      </Stack>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// GeneratePanelV2 — standalone (no form context), uses props for event/measurement
// ---------------------------------------------------------------------------

const GeneratePanelV2 = ({
  eventId,
  measurementId,
  label,
  effectiveDate,
  onBack,
  onSaved,
}: {
  eventId: string;
  measurementId: string;
  label: string;
  effectiveDate: string;
  onBack: () => void;
  onSaved: (newId: string) => void;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [maxPts, setMaxPts] = useState(1000);
  const [minPts, setMinPts] = useState(0);
  const [topResult, setTopResult] = useState<number | ''>('');
  const [bottomResult, setBottomResult] = useState<number | ''>('');
  const [preview, setPreview] = useState<ReturnType<typeof generateScoreTableRows> | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handlePreview = () => {
    setPreviewError(null);
    if (topResult === '' || bottomResult === '') {
      setPreviewError('Enter both Top Result and Bottom Result anchors.');
      return;
    }
    try {
      const rows = generateScoreTableRows({
        max_points: maxPts,
        min_points: minPts,
        top_result: Number(topResult),
        bottom_result: Number(bottomResult),
      });
      setPreview(rows);
    } catch (err: unknown) {
      setPreviewError(err instanceof Error ? err.message : 'Unknown error');
    }
  };

  const handleSave = async () => {
    if (!preview) return;
    setSaving(true);
    try {
      const result = await submitGenerate(dataProvider, {
        event_id: eventId,
        measurement_id: measurementId,
        label,
        effective_date: effectiveDate,
        max_points: maxPts,
        min_points: minPts,
        top_result: Number(topResult),
        bottom_result: Number(bottomResult),
      });
      onSaved(result.data.id);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      notify(`Save failed: ${msg}`, { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        Generate table from D10 piecewise-linear curve
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Set terminal points and anchor results. Every row applies without demographic adjustment.
      </Typography>

      <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 2 }}>
        <MuiTextField
          label="Max Points (top anchor earns)"
          type="number"
          value={maxPts}
          onChange={(e) => setMaxPts(Number(e.target.value))}
          size="small"
          sx={{ width: 200 }}
        />
        <MuiTextField
          label="Min Points (bottom anchor earns)"
          type="number"
          value={minPts}
          onChange={(e) => setMinPts(Number(e.target.value))}
          size="small"
          sx={{ width: 200 }}
        />
        <MuiTextField
          label="Top Result anchor (earns max pts)"
          type="number"
          value={topResult}
          onChange={(e) => setTopResult(e.target.value === '' ? '' : Number(e.target.value))}
          size="small"
          helperText="Smaller = lower-is-better (times)"
          sx={{ width: 240 }}
        />
        <MuiTextField
          label="Bottom Result anchor (earns min pts)"
          type="number"
          value={bottomResult}
          onChange={(e) => setBottomResult(e.target.value === '' ? '' : Number(e.target.value))}
          size="small"
          sx={{ width: 240 }}
        />
      </Stack>

      {previewError && <Alert severity="error" sx={{ mb: 2 }}>{previewError}</Alert>}

      <Stack direction="row" spacing={1} mb={2}>
        <Button label="Back" onClick={onBack} variant="outlined" size="small" />
        <Button
          label="Preview"
          startIcon={<AutoFixHighIcon />}
          onClick={handlePreview}
          variant="outlined"
          size="small"
        />
        {preview && (
          <Button
            label={saving ? 'Saving…' : `Save (${preview.length} rows)`}
            startIcon={<CheckCircleIcon />}
            onClick={handleSave}
            disabled={saving}
            variant="contained"
            size="small"
          />
        )}
      </Stack>

      {preview && (
        <Box mt={2} sx={{ maxHeight: 400, overflowY: 'auto' }}>
          <Typography variant="caption" color="text.secondary">
            Showing {preview.length} bands. Clamp rows included.
          </Typography>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Result Min</TableCell>
                <TableCell>Result Max</TableCell>
                <TableCell>Points</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {preview.slice(0, 200).map((row, i) => (
                <TableRow key={i}>
                  <TableCell>{row.result_min}</TableCell>
                  <TableCell>{row.result_max >= 9_000_000 ? '∞' : row.result_max}</TableCell>
                  <TableCell>{row.points}</TableCell>
                </TableRow>
              ))}
              {preview.length > 200 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Typography variant="caption">… {preview.length - 200} more rows</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {topResult !== '' && bottomResult !== '' && (
            <D10CurveChart
              topResult={Number(topResult)}
              bottomResult={Number(bottomResult)}
              minPts={minPts}
              maxPts={maxPts}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// CSV parser helper
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// CsvPanelV2 — standalone CSV import (create flow)
// ---------------------------------------------------------------------------

const CsvPanelV2 = ({
  eventId,
  measurementId,
  label,
  effectiveDate,
  onBack,
  onSaved,
}: {
  eventId: string;
  measurementId: string;
  label: string;
  effectiveDate: string;
  onBack: () => void;
  onSaved: (newId: string) => void;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [csvText, setCsvText] = useState('');
  const [preview, setPreview] = useState<CoverageRow[] | null>(null);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [importError, setImportError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setCsvText((ev.target?.result as string) ?? '');
      setPreview(null);
    };
    reader.readAsText(file);
  };

  const handleParsePreview = () => {
    setParseErrors([]);
    setPreview(null);
    const { parsed, errors } = parseScoreTableCsv(csvText);
    setParseErrors(errors);
    if (parsed.length > 0) setPreview(parsed);
  };

  const handleImport = async () => {
    if (!csvText.trim()) {
      notify('Paste or upload CSV before importing', { type: 'warning' });
      return;
    }
    const validation = parseScoreTableCsv(csvText);
    if (validation.errors.length > 0 || validation.parsed.length === 0) {
      setParseErrors(validation.errors);
      setPreview(null);
      return;
    }
    setSaving(true);
    setImportError(null);
    try {
      const result = await submitCsvImport(dataProvider, {
        event_id: eventId,
        measurement_id: measurementId,
        label,
        effective_date: effectiveDate,
        csv: csvText,
      });
      onSaved(result.data.id);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setImportError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>Import scoring bands from CSV</Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Required columns: <code>result_min, result_max, points</code>. Demographic score columns are rejected.
      </Typography>

      <Box mb={2}>
        <input type="file" accept=".csv" onChange={handleFileChange} />
      </Box>

      <MuiTextField
        multiline
        rows={8}
        fullWidth
        variant="outlined"
        placeholder={'result_min,result_max,points\n0,10,100'}
        value={csvText}
        onChange={(e) => setCsvText(e.target.value)}
        sx={{ fontFamily: 'monospace', mb: 2 }}
        label="Or paste CSV here"
      />

      {parseErrors.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {parseErrors.map((e, i) => (
            <div key={i}>{e}</div>
          ))}
        </Alert>
      )}

      {importError && <Alert severity="error" sx={{ mb: 2 }}>{importError}</Alert>}

      {preview && (
        <Box mb={2} sx={{ maxHeight: 300, overflowY: 'auto' }}>
          <Typography variant="subtitle2" gutterBottom>
            Preview ({preview.length} rows parsed)
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Result Min</TableCell>
                <TableCell>Result Max</TableCell>
                <TableCell>Points</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {preview.map((row, i) => (
                <TableRow key={i}>
                  <TableCell>{row.result_min}</TableCell>
                  <TableCell>{row.result_max}</TableCell>
                  <TableCell>{row.points}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <Stack direction="row" spacing={1}>
        <Button label="Back" onClick={onBack} variant="outlined" size="small" />
        <Button
          label="Parse & Preview"
          onClick={handleParsePreview}
          variant="outlined"
          size="small"
        />
        <Button
          label={saving ? 'Importing…' : 'Confirm and Import'}
          startIcon={<UploadFileIcon />}
          onClick={handleImport}
          disabled={saving || !csvText.trim()}
          variant="contained"
          size="small"
        />
      </Stack>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// ScoreTableCreate — controlled component outside react-admin form machinery
// ---------------------------------------------------------------------------

const ScoreTableCreate = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const prefillEventId = params.get('event_id') ?? '';
  const prefillMeasurementId = params.get('measurement_id') ?? '';

  const [eventId, setEventId] = useState(prefillEventId);
  const [measurementId, setMeasurementId] = useState(prefillMeasurementId);
  const [label, setLabel] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState<null | 'manual' | 'generate' | 'csv'>(null);

  const eventLocked = !!prefillEventId;
  const measurementLocked = !!prefillMeasurementId;

  const { data: event } = useGetOne('events', { id: eventId }, { enabled: !!eventId });
  const { data: eventsResult } = useGetList('events', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
  });
  const events = eventsResult ?? [];
  const measurements: MeasurementDef[] = event?.measurements ?? [];

  const lockedMeasurementName = prefillMeasurementId
    ? (event?.measurements ?? []).find((m: MeasurementDef) => m.measurement_id === prefillMeasurementId)?.name
    : '';

  const headerComplete = !!eventId && !!measurementId;

  const handleSaved = (newId: string) => {
    notify('Scoring table saved', { type: 'success' });
    redirect('show', 'score_tables', newId);
  };

  const methodCards = [
    {
      key: 'manual' as const,
      icon: <EditIcon fontSize="large" />,
      title: 'Enter bands manually',
      description: 'Type result ranges and points directly. Minimum one row required.',
    },
    {
      key: 'generate' as const,
      icon: <AutoFixHighIcon fontSize="large" />,
      title: 'Generate from anchor results (D10 curve)',
      description:
        'Set top and bottom anchor results; the D10 piecewise-linear formula fills in all bands.',
    },
    {
      key: 'csv' as const,
      icon: <UploadFileIcon fontSize="large" />,
      title: 'Import scoring CSV',
      description: 'Upload tenant-owned result ranges and points. Demographic score columns are rejected.',
    },
  ];

  return (
    <Create>
      <Box p={3} maxWidth={900}>
        <Typography variant="h5" gutterBottom>
          New Scoring Table
        </Typography>

        {/* Header: shared across all methods */}
        <Stack spacing={2} mb={3}>
          {eventLocked ? (
            <Box>
              <Typography variant="caption" color="text.secondary">Event</Typography>
              <Typography variant="body1">{event?.name ?? prefillEventId}</Typography>
            </Box>
          ) : (
            <MuiTextField
              select
              label="Event"
              value={eventId}
              onChange={(e) => {
                setEventId(e.target.value);
                setMeasurementId('');
              }}
              fullWidth
            >
              {events.map((ev: { id: string; name?: string }) => (
                <MenuItem key={ev.id} value={ev.id}>
                  {ev.name ?? ev.id}
                </MenuItem>
              ))}
            </MuiTextField>
          )}

          {measurementLocked ? (
            <Box>
              <Typography variant="caption" color="text.secondary">Measurement</Typography>
              <Typography variant="body1">{lockedMeasurementName ?? prefillMeasurementId}</Typography>
            </Box>
          ) : (
            <MuiTextField
              select
              label={!eventId ? 'Select an event first' : 'Measurement'}
              value={measurementId}
              onChange={(e) => setMeasurementId(e.target.value)}
              disabled={!eventId || measurements.length === 0}
              fullWidth
            >
              {measurements.map((m) => (
                <MenuItem key={m.measurement_id} value={m.measurement_id}>
                  {m.name}
                </MenuItem>
              ))}
            </MuiTextField>
          )}

          <MuiTextField
            label="Label (e.g. annual standard)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            fullWidth
          />

          <MuiTextField
            label="Effective Date"
            type="date"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 200 }}
          />
        </Stack>

        {/* Method chooser or method panel */}
        {!method && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Choose a method
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              {methodCards.map((card) => (
                <Tooltip
                  key={card.key}
                  title={!headerComplete ? 'Select an event and measurement first' : ''}
                  disableHoverListener={headerComplete}
                >
                  <span>
                    <Card sx={{ width: 240, opacity: headerComplete ? 1 : 0.5 }}>
                      <CardActionArea
                        onClick={() => headerComplete && setMethod(card.key)}
                        disabled={!headerComplete}
                      >
                        <CardContent>
                          <Box mb={1}>{card.icon}</Box>
                          <Typography variant="subtitle2" gutterBottom>
                            {card.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {card.description}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </span>
                </Tooltip>
              ))}
            </Stack>
          </Box>
        )}

        {method === 'manual' && (
          <ManualBandsPanel
            eventId={eventId}
            measurementId={measurementId}
            label={label}
            effectiveDate={effectiveDate}
            onBack={() => setMethod(null)}
            onSaved={handleSaved}
          />
        )}

        {method === 'generate' && (
          <GeneratePanelV2
            eventId={eventId}
            measurementId={measurementId}
            label={label}
            effectiveDate={effectiveDate}
            onBack={() => setMethod(null)}
            onSaved={handleSaved}
          />
        )}

        {method === 'csv' && (
          <CsvPanelV2
            eventId={eventId}
            measurementId={measurementId}
            label={label}
            effectiveDate={effectiveDate}
            onBack={() => setMethod(null)}
            onSaved={handleSaved}
          />
        )}
      </Box>
    </Create>
  );
};

// ---------------------------------------------------------------------------
// Approval panel — shown on pending_approval tables
// ---------------------------------------------------------------------------

const ApprovalPanel = () => {
  const record = useRecordContext<ScoreTable>();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [acting, setActing] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  if (hasDemographicScoreFields(record?.rows)) {
    return <Alert severity="warning">Approval is unavailable because this historical demographic-scored table is ineligible for active use.</Alert>;
  }
  if (!record?.id || record.status !== 'pending_approval') return null;

  const gen = record.generator;

  const approve = async () => {
    setActing(true);
    try {
      await dataProvider.create(`score_tables/${record.id}/approve`, { data: {} });
      notify('Table approved', { type: 'success' });
      setApproveDialogOpen(false);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      notify(`Failed: ${msg}`, { type: 'error' });
    } finally {
      setActing(false);
    }
  };

  const reject = async () => {
    const reason = rejectReason.trim();
    if (!reason) return;
    setActing(true);
    try {
      await dataProvider.create(`score_tables/${record.id}/reject`, { data: { reason } });
      notify('Table rejected', { type: 'success' });
      setRejectDialogOpen(false);
      setRejectReason('');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      notify(`Failed: ${msg}`, { type: 'error' });
    } finally {
      setActing(false);
    }
  };

  return (
    <Box mt={2}>
      <Alert severity="warning" sx={{ mb: 2 }}>
        This is a recalibration proposal pending Master Admin approval. It has no effective date
        and will not be used for scoring until approved.
      </Alert>

      {gen && (
        <Box mb={2}>
          <Typography variant="subtitle2" gutterBottom>
            Proposed Parameters
          </Typography>
          <Stack direction="row" spacing={4} flexWrap="wrap">
            <Box>
              <Typography variant="caption" color="text.secondary">
                Terminal Points
              </Typography>
              <Typography variant="body2">
                {gen.min_points} – {gen.max_points}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Top Anchor
              </Typography>
              <Typography variant="body2">{gen.top_result}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Bottom Anchor
              </Typography>
              <Typography variant="body2">{gen.bottom_result}</Typography>
            </Box>
            {gen.distribution && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Distribution (n={gen.distribution.count})
                </Typography>
                <Typography variant="body2">
                  p1={gen.distribution.p1.toFixed(1)} p25={gen.distribution.p25.toFixed(1)}{' '}
                  p75={gen.distribution.p75.toFixed(1)} p99={gen.distribution.p99.toFixed(1)}
                </Typography>
              </Box>
            )}
          </Stack>
          {gen.prior_table_id && (
            <Typography variant="caption" color="text.secondary">
              Replaces table: {gen.prior_table_id}
            </Typography>
          )}
        </Box>
      )}

      <Stack direction="row" spacing={1}>
        <Button
          label={acting ? 'Processing…' : 'Approve'}
          startIcon={<CheckCircleIcon />}
          onClick={() => setApproveDialogOpen(true)}
          disabled={acting}
          variant="contained"
          color="success"
          size="small"
        />
        <Button
          label={acting ? 'Processing…' : 'Reject'}
          startIcon={<CancelIcon />}
          onClick={() => setRejectDialogOpen(true)}
          disabled={acting}
          variant="outlined"
          color="error"
          size="small"
        />
      </Stack>

      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)}>
        <DialogTitle>Approve score table?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will activate the table and supersede any currently active version for this
            event/measurement. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button label="Cancel" onClick={() => setApproveDialogOpen(false)} disabled={acting} />
          <Button
            label={acting ? 'Processing…' : 'Confirm'}
            onClick={approve}
            disabled={acting}
            variant="contained"
            color="success"
          />
        </DialogActions>
      </Dialog>

      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject score table?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Provide a reason for rejecting this score table.
          </DialogContentText>
          <MuiTextField
            label="Reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            required
            fullWidth
            multiline
            minRows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button label="Cancel" onClick={() => setRejectDialogOpen(false)} disabled={acting} />
          <Button
            label={acting ? 'Processing…' : 'Reject'}
            onClick={reject}
            disabled={acting || rejectReason.trim().length === 0}
            variant="contained"
            color="error"
          />
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Generator provenance panel — shown on generated tables
// ---------------------------------------------------------------------------

const ProvenancePanel = () => {
  const record = useRecordContext<ScoreTable>();
  if (!record?.generator) return null;

  const gen = record.generator;
  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Generator Parameters
      </Typography>
      <Stack direction="row" spacing={4} flexWrap="wrap" mb={1}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Terminal Points
          </Typography>
          <Typography variant="body2">
            {gen.min_points} – {gen.max_points}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Top Anchor
          </Typography>
          <Typography variant="body2">{gen.top_result}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Bottom Anchor
          </Typography>
          <Typography variant="body2">{gen.bottom_result}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Curve
          </Typography>
          <Typography variant="body2">{gen.curve}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Breakpoints
          </Typography>
          <Typography variant="body2">
            p25={gen.breakpoints.p25_points} / p75={gen.breakpoints.p75_points}
          </Typography>
        </Box>
        {gen.result_count_at_generation != null && (
          <Box>
            <Typography variant="caption" color="text.secondary">
              Result Count
            </Typography>
            <Typography variant="body2">{gen.result_count_at_generation}</Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// VersionHistorySection
// ---------------------------------------------------------------------------

const VersionHistorySection = () => {
  const record = useRecordContext<ScoreTable>();
  const { data: allVersions } = useGetList<ScoreTable>('score_tables', {
    filter: { event_id: record?.event_id },
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'effective_date', order: 'DESC' },
  });

  if (!record) return null;

  const related = sortVersionHistory(
    (allVersions ?? []).filter((r) => r.measurement_id === record.measurement_id),
  );

  return (
    <Box mt={2}>
      <Typography variant="subtitle2" gutterBottom>
        All versions for this event + measurement
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Effective Date</TableCell>
            <TableCell>Version</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Rows</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {related.map((r) => (
            <TableRow
              key={r.id}
              sx={
                r.id === record.id
                  ? { fontWeight: 'bold', backgroundColor: 'action.selected' }
                  : undefined
              }
            >
              <TableCell>{r.effective_date ?? '—'}</TableCell>
              <TableCell>{(r.version ?? '').slice(0, 16)}</TableCell>
              <TableCell>
                <ScoreTableStatus record={r as ScoreTable} />
              </TableCell>
              <TableCell>{r.rows?.length ?? 0}</TableCell>
              <TableCell>
                {r.id === record.id ? (
                  <Typography variant="caption" color="text.secondary">
                    current
                  </Typography>
                ) : (
                  <Link to={`/score_tables/${r.id}/show`}>View</Link>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// SaveAsNewVersionToolbar
// ---------------------------------------------------------------------------

const SaveAsNewVersionToolbar = () => (
  <Toolbar>
    <Box>
      <SaveButton label="Save as new version" />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        The current version is kept and marked superseded.
      </Typography>
    </Box>
  </Toolbar>
);

// ---------------------------------------------------------------------------
// CsvEditImportPanel — for the Edit view, posts to import-csv with record context
// ---------------------------------------------------------------------------

const CsvEditImportPanel = () => {
  const record = useRecordContext<ScoreTable>();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const [csv, setCsv] = useState('');
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  if (!record?.id) return null;

  const handleImport = async () => {
    if (!csv.trim()) {
      notify('Paste CSV text before importing', { type: 'warning' });
      return;
    }
    const validation = parseScoreTableCsv(csv);
    if (validation.errors.length > 0 || validation.parsed.length === 0) {
      setImportError(validation.errors.join(' '));
      return;
    }
    setImporting(true);
    setImportError(null);
    try {
      const result = await submitCsvImport(dataProvider, {
        event_id: record.event_id,
        measurement_id: record.measurement_id,
        label: record.label ?? 'Imported',
        effective_date: record.effective_date ?? new Date().toISOString().slice(0, 10),
        csv,
      });
      notify('Scoring table imported as a new version', { type: 'success' });
      redirect('show', 'score_tables', result.data.id);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setImportError(msg);
    } finally {
      setImporting(false);
    }
  };

  return (
    <Box mt={2}>
      <Typography variant="subtitle1" gutterBottom>
        Import rows from CSV (creates a new version)
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Required columns: <code>result_min, result_max, points</code>. Demographic score columns are rejected.
        Result values must be in the measurement's base unit.
      </Typography>

      {importError && <Alert severity="error" sx={{ mb: 2 }}>{importError}</Alert>}

      <MuiTextField
        multiline
        rows={8}
        fullWidth
        variant="outlined"
        placeholder={'result_min,result_max,points\n0,10,100'}
        value={csv}
        onChange={(e) => setCsv(e.target.value)}
        sx={{ fontFamily: 'monospace', mb: 1 }}
      />
      <Button
        label={importing ? 'Importing…' : 'Import CSV as new version'}
        startIcon={<UploadFileIcon />}
        onClick={handleImport}
        disabled={importing}
        variant="contained"
        size="small"
      />
    </Box>
  );
};

// ---------------------------------------------------------------------------
// GeneratePanel — used inside Edit form (reads event_id/measurement_id from form)
// ---------------------------------------------------------------------------

const GeneratePanel = ({ initialEffectiveDate }: { initialEffectiveDate?: string }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const eventId = (useWatch({ name: 'event_id' }) as string | undefined) ?? '';
  const measurementId = (useWatch({ name: 'measurement_id' }) as string | undefined) ?? '';

  const [maxPts, setMaxPts] = useState(1000);
  const [minPts, setMinPts] = useState(0);
  const [topResult, setTopResult] = useState<number | ''>('');
  const [bottomResult, setBottomResult] = useState<number | ''>('');
  const [effectiveDate, setEffectiveDate] = useState(
    initialEffectiveDate ?? new Date().toISOString().slice(0, 10),
  );
  const [preview, setPreview] = useState<ReturnType<typeof generateScoreTableRows> | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handlePreview = () => {
    setPreviewError(null);
    if (topResult === '' || bottomResult === '') {
      setPreviewError('Enter both Top Result and Bottom Result anchors.');
      return;
    }
    try {
      const rows = generateScoreTableRows({
        max_points: maxPts,
        min_points: minPts,
        top_result: Number(topResult),
        bottom_result: Number(bottomResult),
      });
      setPreview(rows);
    } catch (err: unknown) {
      setPreviewError(err instanceof Error ? err.message : 'Unknown error');
    }
  };

  const handleSave = async () => {
    if (!preview) return;
    setSaving(true);
    try {
      await submitGenerate(dataProvider, {
          event_id: eventId,
          measurement_id: measurementId,
          label: 'Generated scoring table',
          effective_date: effectiveDate,
          max_points: maxPts,
          min_points: minPts,
          top_result: Number(topResult),
          bottom_result: Number(bottomResult),
      });
      notify('Generated table saved', { type: 'success' });
      setPreview(null);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      notify(`Save failed: ${msg}`, { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box mt={2}>
      <Typography variant="subtitle1" gutterBottom>
        Generate table from D10 piecewise-linear curve
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Set terminal points and anchor results. Every row applies without demographic adjustment.
      </Typography>

      <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 2 }}>
        <MuiTextField
          label="Max Points (top anchor earns)"
          type="number"
          value={maxPts}
          onChange={(e) => setMaxPts(Number(e.target.value))}
          size="small"
          sx={{ width: 200 }}
        />
        <MuiTextField
          label="Min Points (bottom anchor earns)"
          type="number"
          value={minPts}
          onChange={(e) => setMinPts(Number(e.target.value))}
          size="small"
          sx={{ width: 200 }}
        />
        <MuiTextField
          label="Top Result anchor (earns max pts)"
          type="number"
          value={topResult}
          onChange={(e) => setTopResult(e.target.value === '' ? '' : Number(e.target.value))}
          size="small"
          helperText="Smaller = lower-is-better (times)"
          sx={{ width: 240 }}
        />
        <MuiTextField
          label="Bottom Result anchor (earns min pts)"
          type="number"
          value={bottomResult}
          onChange={(e) => setBottomResult(e.target.value === '' ? '' : Number(e.target.value))}
          size="small"
          sx={{ width: 240 }}
        />
      </Stack>

      <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 2 }}>
        <MuiTextField
          label="Effective Date"
          type="date"
          value={effectiveDate}
          onChange={(e) => setEffectiveDate(e.target.value)}
          size="small"
          sx={{ width: 180 }}
          InputLabelProps={{ shrink: true }}
        />
      </Stack>

      {previewError && <Alert severity="error" sx={{ mb: 2 }}>{previewError}</Alert>}

      <Stack direction="row" spacing={1}>
        <Button
          label="Preview"
          startIcon={<AutoFixHighIcon />}
          onClick={handlePreview}
          variant="outlined"
          size="small"
        />
        {preview && (
          <Button
            label={saving ? 'Saving…' : `Save (${preview.length} rows)`}
            startIcon={<CheckCircleIcon />}
            onClick={handleSave}
            disabled={saving}
            variant="contained"
            size="small"
          />
        )}
      </Stack>

      {preview && (
        <Box mt={2} sx={{ maxHeight: 400, overflowY: 'auto' }}>
          <Typography variant="caption" color="text.secondary">
            Showing {preview.length} bands. Clamp rows included.
          </Typography>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Result Min</TableCell>
                <TableCell>Result Max</TableCell>
                <TableCell>Points</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {preview.slice(0, 200).map((row, i) => (
                <TableRow key={i}>
                  <TableCell>{row.result_min}</TableCell>
                  <TableCell>{row.result_max >= 9_000_000 ? '∞' : row.result_max}</TableCell>
                  <TableCell>{row.points}</TableCell>
                </TableRow>
              ))}
              {preview.length > 200 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Typography variant="caption">… {preview.length - 200} more rows</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {topResult !== '' && bottomResult !== '' && (
            <D10CurveChart
              topResult={Number(topResult)}
              bottomResult={Number(bottomResult)}
              minPts={minPts}
              maxPts={maxPts}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// ScoreTableEdit
// ---------------------------------------------------------------------------

const ScoreTableInputPrereqGuard = ({ children }: { children: React.ReactNode }) => {
  const eventId = useWatch({ name: 'event_id' });
  const measurementId = useWatch({ name: 'measurement_id' });

  if (!eventId || !measurementId) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Select an event and measurement before editing scoring inputs.
      </Alert>
    );
  }

  return <>{children}</>;
};

const ScoreTableEditForm = () => {
  const record = useRecordContext<ScoreTable>();
  if (hasDemographicScoreFields(record?.rows)) {
    return <Alert severity="warning">Historical demographic-scored tables are immutable and ineligible. Create a new demographic-neutral table instead.</Alert>;
  }
  return (
    <TabbedForm toolbar={<SaveAsNewVersionToolbar />} warnWhenUnsavedChanges>
      <FormTab label="Details">
        <ReferenceInput perPage={100} label="Event" source="event_id" reference="events">
          <AutocompleteInput optionText="name" />
        </ReferenceInput>
        <DependentMeasurementSelect />
        <TextInput fullWidth label="Label (e.g. annual standard)" source="label" />
        <DateInput fullWidth label="Effective Date" source="effective_date" />
      </FormTab>

      <FormTab label="Scoring Rows">
        <ScoreTableInputPrereqGuard>
          <ArrayInput
            label="Scoring Rows"
            source="rows"
            helperText="Result values must be in the measurement's normalized base unit."
          >
            <SimpleFormIterator>
              <NumberInput label="Result Min (base unit, inclusive)" source="result_min" />
              <NumberInput label="Result Max (base unit, inclusive)" source="result_max" />
              <NumberInput label="Points" source="points" min={0} />
            </SimpleFormIterator>
          </ArrayInput>
        </ScoreTableInputPrereqGuard>
      </FormTab>

      <FormTab label="Generate">
        <ScoreTableInputPrereqGuard>
          <GeneratePanel initialEffectiveDate={record?.effective_date} />
        </ScoreTableInputPrereqGuard>
      </FormTab>

      <FormTab label="Import CSV">
        <ScoreTableInputPrereqGuard>
          <CsvEditImportPanel />
        </ScoreTableInputPrereqGuard>
      </FormTab>

      <FormTab label="Approval">
        <Divider sx={{ mb: 2 }} />
        <ApprovalPanel />
      </FormTab>
    </TabbedForm>
  );
};

const ScoreTableEdit = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  return (
    <Edit
      mutationMode="pessimistic"
      mutationOptions={{
        // A custom onSuccess REPLACES react-admin's default side effects,
        // so we must notify and redirect ourselves. The API's PUT returns a
        // brand-new record (new id) — redirect there, not to the old
        // (now superseded) record.
        onSuccess: (data: { id: string }) => {
          notify('Saved as a new version — the previous version was marked superseded', {
            type: 'success',
          });
          redirect('show', 'score_tables', data.id);
        },
      }}
    >
      <ScoreTableEditForm />
    </Edit>
  );
};

// ---------------------------------------------------------------------------
// ScoreTableShow
// ---------------------------------------------------------------------------

const ScoreTableShow = () => (
  <Show>
    <TabbedShowLayout>
      <Tab label="Summary">
        <TextField label="Effective Date" source="effective_date" />
        <TextField label="Version" source="version" />
        <FunctionField
          label="Status"
          render={(record: ScoreTable) => <ScoreTableStatus record={record} />}
        />
        <ReferenceField label="Event" source="event_id" reference="events">
          <TextField source="name" />
        </ReferenceField>
        <FunctionField label="Measurement" render={() => <MeasurementNameField />} />
        <TextField label="Label" source="label" />
        <FunctionField
          label="Source"
          render={(record: ScoreTable) => record?.source ?? 'manual'}
        />
        <FunctionField
          label="Result Count"
          render={(record: ScoreTable) => record?.result_count ?? 0}
        />
        <HistoricalDemographicNotice />
      </Tab>
      <Tab label="Scoring Rows">
        <ScoreTableRowsField />
      </Tab>
      <Tab label="Generator">
        <ProvenancePanel />
      </Tab>
      <Tab label="Approval">
        <ApprovalPanel />
      </Tab>
      <Tab label="Version History">
        <VersionHistorySection />
      </Tab>
    </TabbedShowLayout>
  </Show>
);

// ---------------------------------------------------------------------------
// ScoreTableList
// ---------------------------------------------------------------------------

const ScoreTableList = () => {
  const { permissions } = usePermissions<Permissions>();
  return (
    <RequireEvents>
    <List
      exporter={false}
      filters={[<ArchivedSelect key="archived" />]}
      sort={{ field: 'effective_date', order: 'DESC' }}
      perPage={25}
    >
      <Datagrid rowClick={(_id, _resource, record) => can(permissions, 'manage_scoring_tables') && !hasDemographicScoreFields((record as ScoreTable)?.rows) ? 'edit' : 'show'}>
        <ReferenceField label="Event" source="event_id" reference="events">
          <TextField source="name" />
        </ReferenceField>
        <FunctionField label="Measurement" render={() => <MeasurementNameField />} />
        <TextField label="Label" source="label" />
        <FunctionField
          label="Status"
          render={(record: ScoreTable) => <ScoreTableStatus record={record} />}
        />
        <TextField label="Effective Date" source="effective_date" />
        <FunctionField
          label="Version"
          render={(record: ScoreTable) => (record?.version ?? '').slice(0, 10)}
        />
        <FunctionField
          label="Rows"
          render={(record: ScoreTable) => record?.rows?.length ?? 0}
        />
      </Datagrid>
    </List>
    </RequireEvents>
  );
};

export const score_tables = {
  list: ScoreTableList,
  show: ScoreTableShow,
  create: ScoreTableCreate,
  edit: ScoreTableEdit,
};
